import React, { useState, useEffect } from "react";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import { useBookmarks } from "../../hooks/useBookmarks";
import { useChatService } from "../../services/chatService";
import ShopCard from "../../components/ui/cards/ShopCard";
// import DealCard from "../../components/ui/cards/DealCard"; // Deals deprecated
import {
  User,
  Mail,
  Phone,
  MapPin,
  Car,
  Bell,
  Save,
  Edit3,
  Heart
} from "lucide-react";

interface CustomerProfile {
  id: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  tyre_make_preference?: string;
  tyre_size?: string;
  vehicle_manufacturer?: string;
  vehicle_model?: string;
  vehicle_year?: string;
  marketing_opt_in?: boolean;
  created_at?: string;
}

const CustomerProfilePage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { bookmarks, loading: bookmarksLoading, getBookmarksByType } = useBookmarks();
  const { checkWhatsAppStatus, generateWhatsAppLink } = useChatService();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<CustomerProfile>>({});
  const [bookmarkedBusinesses, setBookmarkedBusinesses] = useState<any[]>([]);
  // const [bookmarkedDeals, setBookmarkedDeals] = useState<any[]>([]); // Deals deprecated
  const [isScrolled, setIsScrolled] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState({
    registered: false,
    verified: false,
    phoneNumber: undefined as string | undefined,
    referralCode: undefined as string | undefined
  });

  useEffect(() => {
    if (user) {
      fetchCustomerData();
      fetchWhatsAppStatus();
    }
  }, [user]);

  const fetchWhatsAppStatus = async () => {
    if (!user?.id) return;
    try {
      const status = await checkWhatsAppStatus(user.id);
      setWhatsappStatus({
        registered: status.registered,
        verified: status.verified,
        phoneNumber: status.phoneNumber || undefined,
        referralCode: status.referralCode || undefined
      });
    } catch (error) {
      console.error('Failed to check WhatsApp status:', error);
    }
  };

  useEffect(() => {
    if (bookmarks.length > 0) {
      fetchBookmarkedBusinessData();
    }
  }, [bookmarks]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchBookmarkedBusinessData = async () => {
    try {
      const shopBookmarks = getBookmarksByType('shop');
      // const dealBookmarks = getBookmarksByType('deal'); // Deals deprecated

      // Fetch business data for shop bookmarks
      if (shopBookmarks.length > 0) {
        const businessIds = shopBookmarks.map(b => b.business_id).filter(Boolean) as string[];
        if (businessIds.length > 0) {
          const { data: businesses } = await supabase
            .from('businesses')
            .select('*')
            .in('id', businessIds)
            .eq('business_status', 'active'); // Only show active businesses
          setBookmarkedBusinesses(businesses || []);
        }
      }

      // Fetch deal data for deal bookmarks
      // TODO: Deals feature is deprecated - remove this section
      // if (dealBookmarks.length > 0) {
      //   const dealIds = dealBookmarks.map(b => b.deal_id).filter(Boolean);
      //   if (dealIds.length > 0) {
      //     const { data: deals } = await supabase
      //       .from('deals')
      //       .select('*')
      //       .in('id', dealIds);
      //     setBookmarkedDeals(deals || []);
      //   }
      // }
    } catch (error) {
      console.error('Error fetching bookmarked business data:', error);
    }
  };

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      if (!user?.id) return;
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      // Merge preferences into the main data object for easier form handling
      const customerProfile = {
        ...data,
        ...(data.preferences && typeof data.preferences === 'object' ? data.preferences : {})
      };

      // Convert null values to undefined for form compatibility
      const formCompatibleProfile = Object.fromEntries(
        Object.entries(customerProfile).map(([key, value]) => [key, value === null ? undefined : value])
      );

      setFormData(formCompatibleProfile as Partial<CustomerProfile>);
    } catch (error) {
      console.error('Error fetching customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      
      // Separate core profile fields from preferences
      const { tyre_size, tyre_make_preference, vehicle_manufacturer, vehicle_model, vehicle_year, marketing_opt_in, first_name, last_name, address, city, postal_code, ...coreFields } = formData;
      
      // Store custom fields in preferences JSONB
      const preferences = {
        tyre_size,
        tyre_make_preference,
        vehicle_manufacturer,
        vehicle_model,
        vehicle_year,
        marketing_opt_in,
        first_name,
        last_name,
        address,
        city,
        postal_code
      };

      // Update with core fields and preferences
      const updateData = {
        ...coreFields,
        preferences,
        full_name: `${first_name || ''} ${last_name || ''}`.trim() || coreFields.full_name
      };

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;

      setEditMode(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* My Profile Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <User className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
              <p className="text-gray-600">Manage your account information and preferences</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/chat')}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
              </svg>
              <span>AI Chat</span>
            </button>
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit3 className="h-4 w-4" />
                <span>Edit Profile</span>
              </button>
            )}
          </div>
        </div>

        {/* Sticky Save Button - Only when editing */}
        {editMode && (
          <div className={`${isScrolled ? 'fixed top-16 right-4 z-50' : 'flex justify-end mb-4'} transition-all duration-300`}>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center space-x-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 disabled:opacity-50 shadow-lg ${
                isScrolled
                  ? 'px-3 py-2 text-sm scale-75'
                  : 'px-4 py-2'
              }`}
            >
              <Save className={`${isScrolled ? 'h-3 w-3' : 'h-4 w-4'}`} />
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        )}

        {/* Personal Information */}
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-4 w-4 mr-1" />
              Email
            </label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone className="inline h-4 w-4 mr-1" />
              Phone
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="Enter phone number"
            />
          </div>

          {/* WhatsApp Status Section */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <svg className="inline w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
              </svg>
              WhatsApp Integration
            </label>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${whatsappStatus.registered ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                  <span className="text-sm font-medium text-gray-900">
                    {whatsappStatus.registered ? 'Connected to WhatsApp Bot' : 'Not Connected'}
                  </span>
                </div>
                {whatsappStatus.registered && whatsappStatus.verified && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Verified
                  </span>
                )}
              </div>
              
              {whatsappStatus.registered ? (
                <div className="space-y-2">
                  {whatsappStatus.phoneNumber && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">WhatsApp Number:</span> {whatsappStatus.phoneNumber}
                    </div>
                  )}
                  {whatsappStatus.referralCode && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Referral Code:</span> 
                      <span className="font-mono ml-1 bg-white px-2 py-1 rounded border">
                        {whatsappStatus.referralCode}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 mt-3">
                    <button
                      onClick={() => navigate('/chat')}
                      className="inline-flex items-center px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                      Open Chat
                    </button>
                    <button
                      onClick={() => {
                        const shareText = `🎉 Join TyreLink and use my referral code: ${whatsappStatus.referralCode}`
                        const waLink = generateWhatsAppLink(shareText)
                        window.open(waLink, '_blank')
                      }}
                      className="inline-flex items-center px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                      Share Referral
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Connect with our WhatsApp bot for instant tyre recommendations, support, and to earn rewards through our referral program.
                  </p>
                  <button
                    onClick={() => {
                      const registerMessage = 'Hi! I want to register for TyreLink and connect my account.'
                      const whatsappUrl = generateWhatsAppLink(registerMessage)
                      window.open(whatsappUrl, '_blank')
                    }}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                    </svg>
                    Connect WhatsApp
                  </button>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="Enter first name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="Enter last name"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="inline h-4 w-4 mr-1" />
              Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="Enter street address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <input
              type="text"
              name="city"
              value={formData.city || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="Enter city"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postal Code
            </label>
            <input
              type="text"
              name="postal_code"
              value={formData.postal_code || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="Enter postal code"
            />
          </div>
        </div>
      </div>

      {/* Vehicle Information Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          <Car className="inline h-5 w-5 mr-2" />
          Vehicle Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Make
            </label>
            <input
              type="text"
              name="vehicle_manufacturer"
              value={formData.vehicle_manufacturer || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="e.g., Toyota, BMW, Ford"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vehicle Model
            </label>
            <input
              type="text"
              name="vehicle_model"
              value={formData.vehicle_model || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="e.g., Corolla, X3, Focus"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Year
            </label>
            <input
              type="text"
              name="vehicle_year"
              value={formData.vehicle_year || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="e.g., 2020"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tyre Size
            </label>
            <input
              type="text"
              name="tyre_size"
              value={formData.tyre_size || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="e.g., 205/55R16, 225/45R17"
            />
            <p className="text-xs text-gray-500 mt-1">
              Format: Width/Aspect Ratio/Rim Diameter (e.g., 205/55R16)
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Tyre Brand
            </label>
            <input
              type="text"
              name="tyre_make_preference"
              value={formData.tyre_make_preference || ''}
              onChange={handleInputChange}
              disabled={!editMode}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                editMode ? 'bg-white' : 'bg-gray-50'
              }`}
              placeholder="e.g., Bridgestone, Michelin, Continental"
            />
          </div>
        </div>
      </div>

      {/* Preferences Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          <Bell className="inline h-5 w-5 mr-2" />
          Preferences
        </h3>
        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="marketing_opt_in"
              name="marketing_opt_in"
              checked={formData.marketing_opt_in || false}
              onChange={handleInputChange}
              disabled={!editMode}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="marketing_opt_in" className="ml-2 block text-sm text-gray-900">
              I would like to receive marketing emails about deals and promotions
            </label>
          </div>
        </div>
      </div>

      {/* Bookmarks Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          <Heart className="inline h-5 w-5 mr-2" />
          My Bookmarks
        </h3>
        
        {bookmarksLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading bookmarks...</span>
          </div>
        ) : bookmarks.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Heart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">No bookmarks yet</p>
            <p className="text-sm">Start bookmarking shops and deals you love!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Bookmarked Shops */}
            {bookmarkedBusinesses.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-3">Shops ({bookmarkedBusinesses.length})</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bookmarkedBusinesses.map((business) => (
                    <ShopCard
                      key={business.id}
                      shop={{
                        id: business.id,
                        name: business.name,
                        address: business.address || "Address not available",
                        city: business.city || "Unknown City",
                        province: business.province || "Unknown Province",
                        phone: business.contact_phone || "Phone not available",
                        email: business.contact_email || "Email not available",
                        coordinates: { lat: 0, lng: 0 },
                        rating: 4.5,
                        reviewCount: 0,
                        services: business.services ? (Array.isArray(business.services) ? business.services : [business.services]) : ["Tyre Services"],
                        openingHours: business.opening_hours || { "Mon-Fri": "8:00 AM - 5:00 PM" },
                        specialties: ["Tyres"],
                        imageUrl: business.logo_url || "/default-shop.jpg",
                        logo_url: business.logo_url,
                        slug: business.id
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bookmarked Deals - DEPRECATED */}
            {/* {bookmarkedDeals.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-800 mb-3">Deals ({bookmarkedDeals.length})</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bookmarkedDeals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      compact={true}
                    />
                  ))}
                </div>
              </div>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerProfilePage;
